import type { JSX } from 'preact'
import { useEffect, useState } from 'preact/hooks'
import { webinarRoomApi } from '@/api/webinarRoom'
import type { WebinarRoomRecordingPlaybackDto } from '@/api/webinarRoom'
import type { OnlineEventViewDto } from '@/api/webs'
import { headshot } from '@/components/console/campaigns/webHomeModel'
import { isHybridMode } from '@/utils/onlineEventMode'
import { useElementWidth } from '@/webinarRoom/moderator/useElementWidth'
import { usePlayer } from '@/webinarRoom/player/usePlayer'
import { useNow } from '@/webinarRoom/useNow'
import { ViewerSlate } from '@/webinarRoom/viewer/ViewerSlate'
import { ViewerLiveControls } from '@/webinarRoom/viewer/ViewerStages'
import { ViewerWaitingImage } from '@/webinarRoom/viewer/ViewerWaitingImage'
import { ViewerWaitingStage } from '@/webinarRoom/viewer/ViewerWaitingStage'
import { ENTRY_OPENS_MS, previewNow, sessionEndMs, sessionStartMs } from './roomWaiting'
import '@/webinarRoom/room.css'

/** The viewer page's stage is drawn at this size and scaled to the card, so the slate's type sits as viewers see it. */
const STAGE_WIDTH = 960
const STAGE_HEIGHT = 540

export interface RoomBrand {
    primary: string | null
    /** No dark-background logo: the preview draws the light theme viewers open on. */
    logoUrlOnLight: string | null
    companyName: string
}

interface RoomWaitingPreviewProps {
    workspaceId: string
    webId: string
    event: OnlineEventViewDto
    brand: RoomBrand
}

type PreviewPlayback = { status: 'none' } | { status: 'ready'; playback: WebinarRoomRecordingPlaybackDto } | { status: 'failed' }

const FAILED_NOTE = 'The video’s preview didn’t load, so this shows the slate alone. Reload the page to try again.'

/** The ready waiting video's playback, signed for this editor and asked for again whenever the video changes. */
function useWaitingPlayback(workspaceId: string, webId: string, readyVideoId: string | null): PreviewPlayback {
    const [playback, setPlayback] = useState<PreviewPlayback>({ status: 'none' })
    useEffect(() => {
        setPlayback({ status: 'none' })
        if (!readyVideoId) return
        let current = true
        webinarRoomApi.getWaitingVideoPreview(workspaceId, webId).then(
            ({ data, status }) => {
                if (current) setPlayback(status >= 200 && status < 300 && data ? { status: 'ready', playback: data } : { status: 'failed' })
            },
            () => { if (current) setPlayback({ status: 'failed' }) },
        )
        return () => { current = false }
    }, [workspaceId, webId, readyVideoId])
    return playback
}

/**
 * The waiting screen as viewers see it from the moment they are let in (ORB-1118): the room's own slate and waiting
 * stage, in the client's skin and brand colours, with the countdown running from 15:00. With a ready waiting video it
 * plays muted above the countdown and follows the same loop or play-once rule; Unmute and Pause work as they do in
 * the room. An image set instead is drawn straight from its public address, with nothing to ask for and no controls.
 */
export function RoomWaitingPreview({ workspaceId, webId, event, brand }: RoomWaitingPreviewProps) {
    const ready = event.WaitingVideo?.State === 'Ready' ? event.WaitingVideo : null
    const image = event.WaitingImage ?? null
    const playsOnce = event.WaitingVideoPlaysOnce === true
    const source = useWaitingPlayback(workspaceId, webId, ready?.Id ?? null)
    const player = usePlayer()
    const { element, width } = useElementWidth<HTMLDivElement>(true)
    const [openedMs] = useState(() => Date.now())
    const startMs = sessionStartMs(event)
    const now = previewNow(startMs, openedMs, useNow())
    const endMs = sessionEndMs(event)
    const startUtc = startMs === null ? null : new Date(startMs).toISOString()
    const room = {
        Title: event.Title,
        StartUtc: startUtc,
        EndUtc: endMs === null ? null : new Date(endMs).toISOString(),
        EntryOpensUtc: startMs === null ? null : new Date(startMs - ENTRY_OPENS_MS).toISOString(),
        TimeZone: event.TimeZone ?? null,
        ReplayAvailable: false,
    }
    const speakers = (event.Speakers ?? []).map(speaker => ({ Name: speaker.Name, Title: speaker.Title, Company: speaker.CompanyName, PhotoUrl: headshot(speaker) }))
    const slate = (
        <ViewerSlate
            stage="holding"
            room={room}
            logos={{ logoUrlOnLight: brand.logoUrlOnLight, logoUrlOnDark: null }}
            companyName={brand.companyName}
            speakers={speakers}
            hybrid={isHybridMode(event.ModeType)}
            now={now}
        />
    )
    const skin = { '--room-acc': brand.primary ?? undefined } as JSX.CSSProperties

    return (
        <div>
            <div class="vskin" style={skin}>
                <div class="vid rounded-lg" ref={element} aria-label="Preview of the waiting screen">
                    <div style={{ position: 'absolute', left: 0, top: 0, width: `${STAGE_WIDTH}px`, height: `${STAGE_HEIGHT}px`, transform: `scale(${width / STAGE_WIDTH})`, transformOrigin: '0 0', containerType: 'size' }}>
                        {image && <ViewerWaitingImage key={image.Url} url={image.Url} room={room} speakers={speakers} now={now} fallback={slate} />}
                        {!image && (source.status === 'ready' && ready
                            ? (
                                <ViewerWaitingStage
                                    key={`${ready.Id}:${playsOnce}`}
                                    player={player}
                                    video={{ playbackId: source.playback.PlaybackId, token: source.playback.Token, playsOnce }}
                                    room={room}
                                    speakers={speakers}
                                    now={now}
                                    accentColor={brand.primary}
                                    title={room.Title}
                                    fallback={slate}
                                />
                            )
                            : slate)}
                    </div>
                    {!image && <div class="stage-tr"><ViewerLiveControls player={player} fullscreenTarget={element} pausable /></div>}
                </div>
            </div>
            {source.status === 'failed' && <p role="alert" className="mt-2.5 text-sm text-red-600 dark:text-red-400">{FAILED_NOTE}</p>}
        </div>
    )
}
